import type { Book, User } from '../../../../shared/types/index.js';

export const isAuthorOwner = (book: Book, user: User | null): boolean => {
  if (!user || user.role !== 'penulis') return false;
  return book.authorId === user.id || book.authorName.toLowerCase() === user.name.toLowerCase();
};

export const isPublisherOwner = (book: Book, user: User | null): boolean => {
  if (!user || (user.role !== 'penerbit' && user.role !== 'organisasi')) return false;
  return (
    book.publisherId === user.id ||
    Boolean(user.organization && book.publisherName.toLowerCase() === user.organization.toLowerCase()) ||
    book.publisherName.toLowerCase() === user.name.toLowerCase() ||
    Boolean(user.publisherCode && book.uibn.includes(user.publisherCode))
  );
};

export const isOwnerOrAdmin = (book: Book, user: User | null): boolean => {
  if (!user) return false;
  return user.role === 'admin' || isAuthorOwner(book, user) || isPublisherOwner(book, user);
};

export const isAuthorSubmission = (book: Book, user?: User | null): boolean => {
  // 1. If status is waiting for publisher curation, it is an author submission
  if (book.status === 'menunggu_penerbit') return true;

  // 2. If authorId and publisherId exist and are different, it is an author submission
  if (book.authorId && book.publisherId && book.authorId !== book.publisherId) {
    return true;
  }

  // 3. If viewer is a Penerbit and authorId exists and does not match viewer ID, it is an author submission
  if (user && user.role === 'penerbit' && book.authorId && book.authorId !== user.id) {
    return true;
  }

  // 4. Explicit submissionSource flag
  if (book.submissionSource === 'penulis') return true;

  // 5. Fallback check: if authorName is present and differs from publisher name / user name
  if (user && user.role === 'penerbit') {
    const isSelfAuthor = book.authorName.toLowerCase() === user.name.toLowerCase() ||
      (user.organization && book.authorName.toLowerCase() === user.organization.toLowerCase());
    if (!isSelfAuthor && book.authorId) {
      return true;
    }
  }

  return false;
};

export const canEditBookMetadata = (book: Book, user: User | null): boolean => {
  if (!user) return false;

  // Admin can edit any book metadata
  if (user.role === 'admin') return true;

  // Penulis can edit their own draft, rejected, or approved/published books (metadata updates except main title)
  if (user.role === 'penulis') {
    const isOwner = isAuthorOwner(book, user);
    if (!isOwner) return false;
    return book.status === 'draft' || book.status === 'ditolak' || book.status === 'disetujui';
  }

  // Penerbit can edit their own self-registered books when in draft, rejected, or approved/published
  // Penerbit CANNOT edit metadata for books submitted by authors under ANY circumstances!
  if (user.role === 'penerbit') {
    const isPublisher = isPublisherOwner(book, user);
    if (!isPublisher) return false;

    // Strictly block if this book was submitted by an author (draft, rejected, or waiting curation)
    if (isAuthorSubmission(book, user)) {
      return false;
    }

    // Publisher can edit their own self-registered books
    return book.status === 'draft' || book.status === 'ditolak' || book.status === 'disetujui';
  }

  return false;
};

export const canCreateNewEdition = (book: Book, user: User | null): boolean => {
  if (!user) return false;

  // Admin can create editions for any book
  if (user.role === 'admin') return true;

  // Penulis can create editions for their own books
  if (user.role === 'penulis') {
    return isAuthorOwner(book, user);
  }

  // Penerbit can ONLY create/publish editions for their own self-registered books
  // Penerbit CANNOT create/publish editions for author-submitted books under ANY circumstances!
  if (user.role === 'penerbit') {
    const isPublisher = isPublisherOwner(book, user);
    if (!isPublisher) return false;

    if (isAuthorSubmission(book, user)) {
      return false;
    }

    return true;
  }

  return false;
};

export const canDeleteBook = (book: Book, user: User | null): boolean => {
  if (!user) return false;

  // Admin can delete any book
  if (user.role === 'admin') return true;

  // Penulis can delete their own draft or rejected books
  if (user.role === 'penulis') {
    return isAuthorOwner(book, user) && (book.status === 'draft' || book.status === 'ditolak');
  }

  // Penerbit can delete their self-registered draft or rejected books
  if (user.role === 'penerbit') {
    return (
      isPublisherOwner(book, user) &&
      !isAuthorSubmission(book, user) &&
      (book.status === 'draft' || book.status === 'ditolak')
    );
  }

  return false;
};

export const STATUS_PILLS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Semua Status' },
  { id: 'disetujui', label: 'Disetujui / Aktif' },
  { id: 'diajukan', label: 'Diajukan' },
  { id: 'menunggu_penerbit', label: 'Menunggu Penerbit' },
  { id: 'ditolak', label: 'Ditolak' },
  { id: 'draft', label: 'Draft' },
  { id: 'diarsipkan', label: 'Diarsipkan' },
];

export const BOOK_CATEGORIES: Array<{ id: string; name: string }> = [
  { id: 'all', name: 'Semua Kategori' },
  { id: 'Fiksi & Sastra', name: 'Fiksi & Sastra' },
  { id: 'Sains & Teknologi', name: 'Sains & Teknologi' },
  { id: 'Bisnis & Ekonomi', name: 'Bisnis & Ekonomi' },
  { id: 'Sejarah & Budaya', name: 'Sejarah & Budaya' },
  { id: 'Pengembangan Diri', name: 'Pengembangan Diri' },
  { id: 'Pendidikan & Akademik', name: 'Pendidikan & Akademik' },
  { id: 'Agama & Filosofi', name: 'Agama & Filosofi' },
  { id: 'Seni & Desain', name: 'Seni & Desain' },
  { id: 'Komik & Grafis', name: 'Komik & Grafis' },
  { id: 'Anak & Remaja', name: 'Anak & Remaja' },
];
